/**
 * Server Configuration
 * 
 * THIN CLIENT ARCHITECTURE:
 * - Cloud Server: Metadata, state sync, user accounts
 * - Controller Device: UI rendering, local network ops (discovery/control)
 * - No additional hardware required
 * 
 * The controller device (iPhone, Android, browser) handles:
 * - Rendering the cloud-hosted UI
 * - Local network discovery (UPnP/mDNS) - native code on mobile
 * - Local renderer control (UPnP SOAP) - native code on mobile
 * - Audio session management - native code on mobile
 */

// =============================================================================
// CONFIGURATION
// =============================================================================

/**
 * Your Mac's local IP for iOS simulator testing.
 * Find via: ipconfig getifaddr en0
 */
const MAC_LOCAL_IP = '192.168.1.40';

/** Cloud server URL — environment-aware (staging/production/dev) */
import { API_BASE, WS_BASE } from '../lib/environment';
export const CLOUD_WS_URL = WS_BASE;
export const CLOUD_API_URL = API_BASE;

/** Local development server port */
const LOCAL_SERVER_PORT = 3001;

/** Web dev server port */
const WEB_DEV_PORT = 7777;

/** Environment modes */
type ServerMode = 'cloud' | 'local';

/**
 * Server mode:
 * - 'local': Development - connects to local server
 * - 'cloud': Production - connects to cloud server
 */
let currentMode: ServerMode = 'local';

import { detectPlatform } from '../utils/platform';

// =============================================================================
// DETECTION HELPERS
// =============================================================================

function isNativeiOS(): boolean {
  // Unified: delegates to detectPlatform() for native-app-only check.
  // Also keeps the legacy `baton` handler fallback for older native builds.
  if (typeof window === 'undefined') return false;
  const { isIOSNativeApp } = detectPlatform();
  return isIOSNativeApp || !!(window as any).webkit?.messageHandlers?.baton;
}

export function isConnectorAvailable(): boolean {
  return typeof window !== 'undefined' && !!(window as any).__MAESTRO_CONNECTOR__;
}

function isiOSSimulator(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('iphone') || ua.includes('ipad');
}

function getCurrentHostname(): string {
  if (typeof window !== 'undefined' && window.location) {
    return window.location.hostname;
  }
  return 'localhost';
}

function isProduction(): boolean {
  // Vite uses import.meta.env, not process.env
  // @ts-ignore - Vite-specific
  if (typeof import.meta !== 'undefined' && import.meta.env?.PROD) {
    return true;
  }
  // Fallback for Node.js environments
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'production') {
    return true;
  }
  return false;
}

// =============================================================================
// MODE MANAGEMENT
// =============================================================================

/**
 * Set the server mode
 */
export function setServerMode(mode: ServerMode): void {
  currentMode = mode;
  console.log(`[ServerConfig] Mode set to: ${mode}`);
}

/**
 * Get the current server mode
 */
export function getServerMode(): ServerMode {
  return currentMode;
}

/**
 * Auto-detect mode based on environment
 */
export function autoDetectMode(): ServerMode {
  if (typeof process !== 'undefined' && process.env?.REACT_APP_SERVER_MODE) {
    return process.env.REACT_APP_SERVER_MODE as ServerMode;
  }
  
  // Maestro proxies the app through localhost but needs cloud APIs
  if (typeof window !== 'undefined' && (window as any).__MAESTRO_CONNECTOR__) {
    console.log('[ServerConfig] Maestro detected, using cloud mode');
    return 'cloud';
  }

  // If we are being served from a local computer (desktop bridge),
  // ALWAYS use local mode so discovery/control work on the LAN.
  if (typeof window !== 'undefined') {
    const hostname = window.location?.hostname || '';
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isDotLocal = hostname.endsWith('.local');
    const isPrivateIp =
      /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname) ||
      /^192\.168\.\d{1,3}\.\d{1,3}$/.test(hostname) ||
      /^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(hostname);

    if (isLocalhost || isDotLocal || isPrivateIp) {
      console.log('[ServerConfig] Detected local hostname/IP, using local mode');
      return 'local';
    }
  }

  // If running on Cloudflare Pages domain, use cloud
  // NOTE: Do NOT match generic "maestro" substrings here.
  // We often use local hostnames like "maestro.local" for dev, and forcing cloud
  // mode there breaks local discovery/control via the local bridge.
  if (typeof window !== 'undefined') {
    const hostname = window.location?.hostname || '';
    const isPages =
      hostname === 'maestro-web.pages.dev' ||
      hostname.endsWith('.maestro-web.pages.dev') ||
      hostname.endsWith('.pages.dev') ||
      hostname === 'maestrodesktop.com' ||
      hostname === 'www.maestrodesktop.com' ||
      hostname === 'maestromusic.app' ||
      hostname === 'www.maestromusic.app';

    if (isPages) {
      console.log('[ServerConfig] Detected Cloudflare Pages domain, using cloud mode');
      return 'cloud';
    }
  }
  
  // Production builds use cloud
  if (isProduction()) {
    return 'cloud';
  }
  
  // Development uses local
  return 'local';
}

// =============================================================================
// SERVER URL GETTERS
// =============================================================================

/**
 * Get the Cloud WebSocket server URL
 */
export function getCloudWebSocketUrl(): string {
  if (typeof process !== 'undefined' && process.env?.REACT_APP_CLOUD_WS_URL) {
    return process.env.REACT_APP_CLOUD_WS_URL;
  }
  return CLOUD_WS_URL;
}

/**
 * Get the Cloud API URL
 */
export function getCloudApiUrl(): string {
  if (typeof process !== 'undefined' && process.env?.REACT_APP_CLOUD_API_URL) {
    return process.env.REACT_APP_CLOUD_API_URL;
  }
  return CLOUD_API_URL;
}

/**
 * Get auth token from localStorage (if available)
 * Part of Phase 2: Authentication
 */
function getStoredAuthToken(): string | null {
  try {
    const stored = localStorage.getItem('maestro_auth');
    if (stored) {
      const { accessToken } = JSON.parse(stored);
      return accessToken || null;
    }
  } catch (e) {
    // Ignore errors
  }
  return null;
}

/**
 * Get the WebSocket server URL based on current mode
 * Optionally includes auth token as query parameter
 */
export function getWebSocketUrl(includeAuth: boolean = true): string {
  // Environment override
  if (typeof process !== 'undefined' && process.env?.REACT_APP_WS_URL) {
    return process.env.REACT_APP_WS_URL;
  }
  
  let baseUrl: string;
  
  // Cloud mode
  if (currentMode === 'cloud') {
    baseUrl = getCloudWebSocketUrl();
  } else {
    // Local mode - handle iOS simulator
    if (isNativeiOS()) {
      const host = getCurrentHostname();
      if (host === 'localhost' || host === '127.0.0.1') {
        console.log(`[ServerConfig] iOS detected, using MAC_LOCAL_IP: ${MAC_LOCAL_IP}`);
        baseUrl = `ws://${MAC_LOCAL_IP}:${LOCAL_SERVER_PORT}`;
      } else {
        baseUrl = `ws://${host}:${LOCAL_SERVER_PORT}`;
      }
    } else {
      const host = getCurrentHostname();
      if (host !== 'localhost' && host !== '127.0.0.1') {
        baseUrl = `ws://${host}:${LOCAL_SERVER_PORT}`;
      } else {
        baseUrl = `ws://localhost:${LOCAL_SERVER_PORT}`;
      }
    }
  }
  
  // Add auth token as query parameter (Phase 2)
  if (includeAuth) {
    const token = getStoredAuthToken();
    if (token) {
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}token=${encodeURIComponent(token)}`;
    }
  }
  
  return baseUrl;
}

/**
 * Get the HTTP API server URL based on current mode
 */
export function getApiUrl(): string {
  if (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  if (currentMode === 'cloud') {
    return getCloudApiUrl();
  }
  
  // Local mode
  if (isNativeiOS()) {
    const host = getCurrentHostname();
    if (host === 'localhost' || host === '127.0.0.1') {
      return `http://${MAC_LOCAL_IP}:${LOCAL_SERVER_PORT}`;
    }
    return `http://${host}:${LOCAL_SERVER_PORT}`;
  }
  
  const host = getCurrentHostname();
  if (host !== 'localhost' && host !== '127.0.0.1') {
    return `http://${host}:${LOCAL_SERVER_PORT}`;
  }
  
  return `http://localhost:${LOCAL_SERVER_PORT}`;
}

// =============================================================================
// DEBUG
// =============================================================================

/**
 * Get configuration summary for debugging
 */
export function getServerConfigDebug(): Record<string, string | boolean> {
  return {
    mode: currentMode,
    wsUrl: getWebSocketUrl(),
    apiUrl: getApiUrl(),
    cloudWsUrl: getCloudWebSocketUrl(),
    cloudApiUrl: getCloudApiUrl(),
    isNativeiOS: isNativeiOS(),
    isiOSSimulator: isiOSSimulator(),
    isProduction: isProduction(),
    currentHostname: getCurrentHostname(),
    macLocalIp: MAC_LOCAL_IP,
  };
}

// Auto-detect mode on load
if (typeof window !== 'undefined') {
  const detectedMode = autoDetectMode();
  setServerMode(detectedMode);
  console.log('[ServerConfig] Configuration:', getServerConfigDebug());
}

export default {
  setServerMode,
  getServerMode,
  autoDetectMode,
  getWebSocketUrl,
  getApiUrl,
  getCloudWebSocketUrl,
  getCloudApiUrl,
  getServerConfigDebug,
  isConnectorAvailable,
  MAC_LOCAL_IP,
  LOCAL_SERVER_PORT,
  CLOUD_WS_URL,
  CLOUD_API_URL,
};
